import { useState } from "react";
import { useTranslation } from "@localization/useTranslation";
import { useSelectableInputFields } from "@common/hooks/useSelectableItemQueries.ts";
import {
    DateFieldReference,
    DatePeriodConfiguration,
    EntityTemplateField,
    getStandardFieldTranslationKey,
    PeriodUnit,
    TemplateEntityType,
} from "../../EntityTemplate.types.ts";
import {
    calculatePeriod,
    ComputedFieldEntityValues,
    DateValue,
    fromDateOptionId,
    getPeriodUnitTranslationKey,
    isEndBeforeStart,
    parseDatePeriod,
    resolveDateValue,
    toDateOptionId,
} from "./datePeriod.utils.ts";
import { ComputedFieldTemplateEditing } from "./computedField.types.ts";

export interface CustomDatePeriodFieldProps {
    field: EntityTemplateField;
    fieldDescription: string | undefined;
    isLoading: boolean;
    isPreview: boolean;
    errorText?: string;
    templateEntityType: TemplateEntityType;
    computedFieldEditing?: ComputedFieldTemplateEditing;
    entity: ComputedFieldEntityValues;
}

const emptyConfiguration: DatePeriodConfiguration = { from: {}, to: {}, unit: PeriodUnit.Years };
const sampleStart = new Date(2020, 0, 1);
const sampleEnd = new Date(2025, 11, 31);

export function useCustomDatePeriodField(props: CustomDatePeriodFieldProps) {
    const { translateString, translateNumber, translateDate } = useTranslation();
    const savedConfiguration = parseDatePeriod(props.field.configurationData);

    // Kept here until both dates are chosen, since the backend only accepts a period with a from and a to date
    const [draft, setDraft] = useState(savedConfiguration ?? emptyConfiguration);

    const { data: inputFields } = useSelectableInputFields(props.templateEntityType, !props.isPreview);

    const configuration = savedConfiguration ?? emptyConfiguration;
    const fromDate = resolveDateValue(configuration.from, props.entity);
    const toDate = resolveDateValue(configuration.to, props.entity);
    const value = savedConfiguration ? calculatePeriod(fromDate, toDate, configuration.unit) : undefined;

    return {
        fromOptionId: toDateOptionId(draft.from),
        toOptionId: toDateOptionId(draft.to),
        unit: draft.unit,
        dateOptions: props.computedFieldEditing?.dateFields ?? [],
        example: `${translateDate(sampleStart)} → ${translateDate(sampleEnd)} = ${translateNumber(calculatePeriod(sampleStart, sampleEnd, draft.unit)!, { maximumFractionDigits: 2 })} ${getUnitText(draft.unit)}`,
        onFromChange: (optionId: string | null) => changeDraft({ ...draft, from: fromDateOptionId(optionId) }),
        onToChange: (optionId: string | null) => changeDraft({ ...draft, to: fromDateOptionId(optionId) }),
        onUnitChange: (unit: PeriodUnit) => changeDraft({ ...draft, unit }),
        valueText: value === undefined ? "" : translateNumber(value, { maximumFractionDigits: 2 }),
        unitText: getUnitText(configuration.unit),
        isEndBeforeStart: isEndBeforeStart(fromDate, toDate),
        missingDateName: savedConfiguration ? getMissingDateName() : undefined,
        datesDescription: savedConfiguration ? `${describeDate(savedConfiguration.from)} → ${describeDate(savedConfiguration.to)}` : undefined,
    };

    function changeDraft(changedConfiguration: DatePeriodConfiguration) {
        setDraft(changedConfiguration);
        if (isComplete(changedConfiguration)) {
            props.computedFieldEditing?.onDatePeriodChange(props.field.id, changedConfiguration);
        }
    }

    // Both dates missing is an entity not yet worked on rather than something to point out, so only a single missing date is named
    function getMissingDateName() {
        if (hasDate(fromDate) === hasDate(toDate)) {
            return undefined;
        }

        return describeDate(hasDate(fromDate) ? configuration.to : configuration.from);
    }

    function getUnitText(unit: PeriodUnit) {
        return translateString(getPeriodUnitTranslationKey(unit));
    }

    function describeDate(reference: DateFieldReference) {
        if (reference.standardField) {
            return translateString(getStandardFieldTranslationKey(reference.standardField));
        }

        return inputFields?.find((inputField) => inputField.id === reference.customFieldId)?.name ?? "";
    }
}

function hasDate(value: DateValue) {
    return value !== null && value !== undefined && value !== "";
}

function isComplete(configuration: DatePeriodConfiguration) {
    return toDateOptionId(configuration.from) !== null && toDateOptionId(configuration.to) !== null;
}
